<script setup>
import {onMounted, reactive, ref} from "vue";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {ToastUtil} from "@/Utils/ToastUtil";
import {useRoute, useRouter} from 'vue-router';
import {Permissions} from "@/Utils/Permissions";
import VuePdfEmbed from 'vue-pdf-embed';
import 'vue-pdf-embed/dist/styles/textLayer.css';
import axios from "axios";

const route = useRoute();
const router = useRouter();

const data = reactive({
  loading: true,
  pdfUrlLoading: true,
  items: null,
  valid: false,
  markRejected: false,
  rejectedReason: '',
  processingClaim: false,
  clientId: null,
  stateItems: ["AL", "AK", "AS", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FM", "FL", "GA", "GU", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MH", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "MP", "OH", "OK", "OR", "PW", "PA", "PR", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VI", "VA", "WA", "WV", "WI", "WY"],
  claimTypes: ["Fire", "Flood", "Hail", "Hurricane", "Liability", "Water", "Wind", "Other"],
  claimModel: {
    claimNumber: null,
    policyNumber: null,
    type: null,
    city: null,
    state: null,
    streetAddress: null,
    zip: null,
    lossType: null,
    dateOfLoss: null,
    lossDetails: null,
    insuredPerson: null,
    insuredMailingAddress: null,
    insuredHomePhone: null,
    insuredCellPhone: null,
    insuredEmailAddress: null,
    additionalInfo: null,
    pending_claim_number: null,
    body_text: null,
    latitude: null,
    longitude: null,
    claimsRep: null,
    claimsRepEmail: null,
    clientName: null,
    clientId: null
  }
})

const getPendingClaimData = async () => {
  data.loading = true;
  await ApiWrapper.post('pending-claims', 'details', {
    id: route.params.id
  })
      .then((response) => {
        const {pendingClaim} = response;
        data.items = pendingClaim;
        transferValues();
      })
      .catch((e) => {
        ToastUtil.error(e);
      })
      .finally(() => {
        data.loading = false;
      })
}

const transferValues = () => {
  let storedVals = data.items.matched_data_json;
  for (const key in storedVals) {
    if (Object.prototype.hasOwnProperty.call(storedVals, key)) {
      if (Object.prototype.hasOwnProperty.call(data.claimModel, key)) {
        data.claimModel[key] = storedVals[key];
      }
    }
  }
  //attempt to get state and zip if not detected by textract
  if (data.claimModel.state === null || data.claimModel.state.length !== 2) {
    if (data.claimModel.insuredMailingAddress !== null) {
      let addressArr = data.claimModel.insuredMailingAddress.split(' ');
      if (addressArr.length > 1) {
        let state = addressArr.at(-2);
        let zip = addressArr.at(-1);
        let streetAddress = data.claimModel.streetAddress.replace(state, '').replace(zip, '');
        if (data.claimModel.city !== null) {
          streetAddress = streetAddress.replace(data.claimModel.city, '');
        }
        data.claimModel.state = state.replace(',', '').replace(' ', '');
        data.claimModel.zip = zip.replace(',', '').replace(' ', '');
        data.claimModel.streetAddress = streetAddress;
      }
    }
  }
  //Attempt to match loss type to claim type

  data.claimTypes.map(claimType => {
    if(data.claimModel.lossType){
      if (data.claimModel.lossType.toLowerCase() === claimType.toLowerCase()) {
        data.claimModel.type = claimType;
      }
    }
  })
}

const markRejected = async () => {
  if (data.markRejected) {
    if (data.rejectedReason === '') {
      await ToastUtil.error('Please enter a reason for rejecting this pending claim.');
    } else {
      data.processingClaim = true;
      await ApiWrapper.post('pending-claims', 'reject-claim',
          {
            id: data.items.id,
            rejectedReason: data.rejectedReason,
          })
          .then((response) => {
            if (response) {
              if (response.pendingClaimUpdateResponse) {
                ToastUtil.successMsg('Pending claim was marked rejected.');
              }
            }
          })
          .catch((e) => {
            data.processingClaim = false;
            ToastUtil.error(e);
          })
          .finally(() => {
            router.push('/emailimportclaims');
          })
    }
  }
}


const createClaim = async () => {

  let claimData = {
    claim: {number: data.claimModel.claimNumber}
  }

  await getClientId();
  if (data.clientId !== null) {
    claimData.clientId = data.clientId
  }

  //get long/lat data
  let encodedSearch = encodeURI(
      (data.claimModel.streetAddress ? data.claimModel.streetAddress.replaceAll('#', '') : '') +
      `, ` +
      data.claimModel.city +
      `, ` +
      data.claimModel.state +
      ` ` +
      data.claimModel.zip
  )

  const mapboxResponse = await ApiWrapper.getRaw(
      'https://api.mapbox.com/geocoding/v5/mapbox.places/' +
      encodedSearch +
      '.json?access_token=pk.eyJ1IjoiZW50cmVvcyIsImEiOiJja2RydG16MXEwZ3hvMnhuOXVlcHZhcWRvIn0.mlZA3MAQSFupaQbmFjA9QQ'
  )

  const lat = mapboxResponse.features[0].center[1];
  const lng = mapboxResponse.features[0].center[0];

  if (!lat || !lng) {
    ToastUtil.errorMsg('Failed to get latitude or longitude for that address. Please verify address information is accurate.')
    return;
  }

  data.claimModel.longitude = lng.toString()
  data.claimModel.latitude = lat.toString()
  data.loading = true;

  try {
    data.processingClaim = true;
    const approveResponse = await ApiWrapper.post('pending-claims', 'approve-claim', {
      id: data.items.id,
      claim: data.claimModel,
      // emailBody: data.items.body_text,
    })

    const claimUuid = approveResponse.createClaim[0].uuid

    // redirect to list
    ToastUtil.successMsg('Claim was successfully created.');
    const newClaimTab = window.location.origin+'/claim/'+claimUuid;
    window.open(newClaimTab);
    await router.push('/emailimportclaims');

  } catch (e) {
    console.log(e)
    data.processingClaim = false;
    ToastUtil.errorMsg(e.response?.data?.message || e.message);
  } finally {
    data.loading = false;
  }
}

const getClientId = async () => {
  if (data.items.client === 'homefirst') {
    data.clientId = 21;
    data.claimModel.clientName = 'homefirst';
    data.claimModel.clientId = 21;
  }
  else if(data.items.client === 'claimsolution'){
    data.clientId = 154;
    data.claimModel.clientName = 'claimsolution';
    data.claimModel.clientId = 154;
  }
  else if(data.items.client === 'firstchicago'){
    data.clientId = 247;
    data.claimModel.clientName = 'firstchicago';
    data.claimModel.clientId = 247;
  }
  else if (data.items.client !== null) {
    await ApiWrapper.get('clients', 'list-clients')
        .then((response) => {
          if (response && response.clients?.length > 0) {
            response.clients.forEach(client => {
              if (client.name === data.items.client) {
                data.clientId = client.id;
              }
            })
          }
        })
  }
}

onMounted(async () => {
  await getPendingClaimData().then(async (response) => {
    await getClientId();
  })
})

</script>

<template>
  <v-card v-if="!data.loading">
    <v-card-title>
      <v-row>
        <v-col cols="6">
          Data extracted from:
          <br/>
          {{ data.items.original_filename }}
        </v-col>
        <v-col cols="6" class="text-right">
          <RouterLink :to="`/emailimportclaims`" v-if="Permissions.checkPermissions(['claims.create'])">
            <v-btn color="primary" variant="elevated" size="small" class="m-3">
              <v-icon>mdi-arrow-left</v-icon>
              Return to Pending Claims List
            </v-btn>
          </RouterLink>
        </v-col>
      </v-row>
      <v-spacer/>
      <v-divider/>
    </v-card-title>

    <v-card-item>
      <v-row>
        <v-col cols="6">
          <v-row>
            <v-col cols="6">
              <h2>Original File:</h2>
            </v-col>
            <v-col cols="6" class="text-right">
              <v-btn
                  v-if="!data.loading"
                  color="gray"
                  size="small"
                  :href="data.items.pdf_url"
                  target="_blank"
                  rel="noopener noreferrer"
              >
                <v-icon>mdi-magnify</v-icon>
              </v-btn>

            </v-col>
          </v-row>

          <VuePdfEmbed v-if="!data.loading" :source="data.items.pdf_url" text-layer/>
        </v-col>
        <v-divider vertical></v-divider>
        <v-col cols="6">
          <v-row>
            <v-col cols="4" class="text-left">
              <h2>Extracted Values:</h2>
            </v-col>
            <v-col cols="8" class="text-left">
              <v-alert
                  text="Ensure the gathered data for this claim is accurate before submitting it."
                  type="warning"
                  variant="tonal"
              ></v-alert>
            </v-col>
          </v-row>
          <v-form ref="form" v-model="data.valid" lazy-validation>
            <v-row>
              <v-col cols="4">
                <v-text-field
                    v-model="data.claimModel.claimNumber"
                    label="Claim Number"
                    :rules="[v => !!v || 'Claim number is required']"
                ></v-text-field>
              </v-col>
              <v-col cols="4">
                <v-text-field
                    v-model="data.claimModel.policyNumber"
                    label="Policy Number"
                ></v-text-field>
              </v-col>
              <v-col cols="4">
                <v-autocomplete
                    class="px-5"
                    v-model="data.claimModel.type"
                    :items="data.claimTypes"
                    label="Claim Type"
                    auto-select-first
                    multiple
                ></v-autocomplete>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="4">
                <v-text-field
                    v-model="data.claimModel.city"
                    label="City"
                    :rules="[v => !!v || 'City is required']"
                ></v-text-field>
              </v-col>
              <v-col cols="4">
                <v-autocomplete
                    class="px-3"
                    v-model="data.claimModel.state"
                    :items="data.stateItems"
                    label="State"
                    clearable
                    :rules="[v => !!v || 'State is required']"
                ></v-autocomplete>
              </v-col>
              <v-col cols="4">
                <v-text-field
                    v-model="data.claimModel.zip"
                    label="Zip"
                    :rules="[v => !!v || 'Zip is required']"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-text-field
                    v-model="data.claimModel.streetAddress"
                    label="Street Address"
                    :rules="[v => !!v || 'Address is required']"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-text-field
                    v-model="data.claimModel.insuredPerson"
                    label="Insured Person"
                    required
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <v-text-field
                    v-model="data.claimModel.insuredHomePhone"
                    label="Insured Home Phone"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                    v-model="data.claimModel.insuredCellPhone"
                    label="Insured Cell Phone (secondary phone number)"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-text-field
                    v-model="data.claimModel.insuredEmailAddress"
                    label="Insured Email"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-text-field
                    v-model="data.claimModel.insuredMailingAddress"
                    label="Insured Mailing Address"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <v-text-field
                    v-model="data.claimModel.lossType"
                    label="Loss Type"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                    v-model="data.claimModel.dateOfLoss"
                    label="Date of Loss"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-textarea
                    v-model="data.claimModel.lossDetails"
                    label="Additional Info"
                ></v-textarea>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <v-text-field
                    v-model="data.claimModel.claimsRep"
                    label="Claims Representative Name"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                    v-model="data.claimModel.claimsRepEmail"
                    label="Claims Representative Email"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-col>
      </v-row>
      <v-row v-if="data.markRejected">
        <v-col cols="12">
          <v-textarea
              v-model="data.rejectedReason"
              label="Reason to reject this pending claim"
          >

          </v-textarea>
        </v-col>
      </v-row>
      <v-row v-if="data.markRejected">
        <v-col cols="6" class="text-left">
          <v-btn
              color="error" variant="elevated" size="large" class="m-3" @click="data.markRejected = false"
          >
            <v-icon>mdi-close</v-icon>
            Cancel
          </v-btn>
        </v-col>
        <v-col cols="6" class="text-right">
          <v-btn
              color="secondary" variant="elevated" size="large" class="m-3" @click="markRejected"
          >
            <v-icon>mdi-file-cancel-outline</v-icon>
            Reject
          </v-btn>
        </v-col>
      </v-row>
    </v-card-item>
    <v-card-actions>
      <v-row>
        <v-col cols="6" class="text-left">
          <v-btn color="error" variant="elevated" size="large" class="m-3" v-if="!data.markRejected || data.processingClaim" :disabled="data.processingClaim" @click="data.markRejected = true">
            <v-icon>mdi-file-cancel-outline</v-icon>
            Mark Rejected
          </v-btn>
        </v-col>
        <v-col cols="6" class="text-right">
          <v-btn color="success" variant="elevated" size="large" class="m-3" v-if="!data.markRejected" :disabled="data.processingClaim" @click="createClaim">
            <v-icon>mdi-file-check-outline</v-icon>
            Create Claim
          </v-btn>
        </v-col>
      </v-row>
    </v-card-actions>
  </v-card>
  <v-skeleton-loader v-else type="card">Loading Pending Claim Data...</v-skeleton-loader>
</template>

<style scoped>

</style>